import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SessionService, type ListSessionsParams } from '../../services';
import { queryKeys } from '../keys';
import type {
  CreateSessionRequest,
  RevertToHistoryRequest,
  UpdateSessionRequest,
} from '../../domain';

export function useSessions(params?: ListSessionsParams) {
  return useQuery({
    queryKey: queryKeys.sessions(params),
    queryFn: () => SessionService.list(params),
  });
}

export function useSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.session(sessionId ?? ''),
    queryFn: () => SessionService.get(sessionId!),
    enabled: !!sessionId,
  });
}

export function useSessionAssets(sessionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sessionAssets(sessionId ?? ''),
    queryFn: () => SessionService.listAssets(sessionId!),
    enabled: !!sessionId,
  });
}

export function useHistory(sessionId: string | undefined, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: [...queryKeys.history(sessionId ?? ''), { page, pageSize }] as const,
    queryFn: () => SessionService.getHistory(sessionId!, page, pageSize),
    enabled: !!sessionId,
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSessionRequest) => SessionService.create(request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

export function useUpdateSession(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateSessionRequest) => SessionService.update(sessionId, request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
      qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, deleteImages = false }: { sessionId: string; deleteImages?: boolean }) =>
      SessionService.remove(sessionId, deleteImages),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useUndo(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SessionService.undo(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
      qc.invalidateQueries({ queryKey: queryKeys.history(sessionId) });
    },
  });
}

export function useRedo(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SessionService.redo(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
      qc.invalidateQueries({ queryKey: queryKeys.history(sessionId) });
    },
  });
}

export function useRevert(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: RevertToHistoryRequest) => SessionService.revert(sessionId, request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
      qc.invalidateQueries({ queryKey: queryKeys.history(sessionId) });
    },
  });
}
